import { FC, Fragment, useMemo, useState } from 'react';

import { useContent, useImageMapper } from '@eneco/cms';
import { Box, Card, Divider, Grid, Heading, Image, RadioGroup, RadioTile, Stack, Text } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';

import { ContactExpandable, type ContactType } from './contact-methods/ContactExpandable';
import type { ContactMethodsContent, ContactMethodsTopic, ContactMethodsType } from './contact-methods-content.types';
import { getIllustration } from './illustrations';

// The readers take the Sitecore section first and the Sanity name second; see
// `contact-methods-content.types.ts` for why the two lanes name these item fields differently.
const readTopic = (topic: ContactMethodsTopic) => ({
  value: topic.name ?? topic.itemName,
  displayName: topic.displayName ?? topic.itemName,
  title: topic.content?.title ?? topic.title,
  text: topic.content?.text ?? topic.text,
  illustration: topic.options?.illustration ?? topic.illustration,
  typesList: topic.options?.typesList ?? topic.typesList,
});

const readType = (type: ContactMethodsType, index: number): ContactType => {
  const section = typeof type.content === 'object' ? type.content : undefined;
  return {
    key: type.id ?? type._key ?? String(index),
    title: section?.title ?? type.content_title,
    text: section?.text ?? type.content_text,
    // Sitecore sends an empty badge as empty strings; Sanity leaves the fields out.
    badgeText: type.badge?.text ?? type.badge_text ?? '',
    badgeColor: type.badge?.color ?? type.color ?? '',
    openingHoursTitle: type.openingHours?.title ?? type.openingHours_title,
    openingHoursContent: type.openingHours?.content ?? (typeof type.content === 'string' ? type.content : undefined),
    phoneNumber: type.contactMethod?.phoneNumberText ?? type.phoneNumberText,
    buttonText: type.contactMethod?.buttonText ?? type.buttonText,
    chatTopic: type.contactMethod?.chatLink?.chatBotTopic?.chatTopic ?? type.chatLink?.chatTopic,
  };
};

/**
 * `ContactMethods` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/ContactMethods/ContactMethods.tsx` 1:1: a title and
 * text, a radio tile per topic, and once a topic is picked a card listing its contact types, each an
 * expandable with a badge, opening hours and a chat or phone button. The image shows on desktop only.
 * Drops the legacy `wrap()`/Experience Editor `editable` checks — this lane never runs inside the
 * Experience Editor.
 *
 * Kept as the legacy has it: a topic's radio value is its item name and the selected topic is looked
 * up by display name, which are the same on every live item; Sanity carries both as `itemName`.
 */
const ContactMethods: FC = () => {
  const content = useContent<ContactMethodsContent>();
  const mapImage = useImageMapper();
  const topics = useMemo(() => (content.topicsList ?? []).map(readTopic), [content.topicsList]);

  const isDesktop = useMediaQuery('lg');

  const [contactReason, setContactReason] = useState<string | undefined>(undefined);

  const items = useMemo(
    () => topics.find(topic => topic.displayName === contactReason)?.typesList?.map(readType),
    [contactReason, topics],
  );

  const image = mapImage(content.image);
  const showImage = !!image?.src && isDesktop;

  const gridItemColumn = { initial: '1/-1' };

  return (
    <Grid gridTemplateColumns={{ initial: '1/-1', md: showImage ? '8fr 4fr' : '1/-1' }} columnGap="6">
      <Grid.Item>
        <Stack gap="10">
          <Stack.Item>
            <Heading as="h2" size={{ initial: 'XS', md: 'M' }} color="inherit">
              {content.title}
            </Heading>
          </Stack.Item>
          <Grid gap="6" columns={2} rowGap="12">
            <Grid.Item gridColumn={gridItemColumn}>
              <Stack gap="6">
                <Text size="BodyL">{content.text}</Text>
                <RadioGroup
                  direction={{ initial: 'column', md: 'row' }}
                  name="contactReason"
                  onValueChange={setContactReason}
                  wrap={false}
                  value={contactReason}
                  label="">
                  {topics.map(topic => {
                    const Illustration = getIllustration(topic.illustration);
                    return (
                      <RadioTile
                        value={topic.value ?? ''}
                        subtitle={topic.text}
                        key={topic.value}
                        icon={Illustration && <Illustration size="small" color="currentColor" />}>
                        {topic.title ?? ''}
                      </RadioTile>
                    );
                  })}
                </RadioGroup>
              </Stack>
            </Grid.Item>
            <Grid.Item gridColumn={gridItemColumn}>
              <Card>
                {items?.map((item, index) => (
                  <Fragment key={item.key}>
                    <Box paddingX="8" paddingY="8">
                      <ContactExpandable item={item} />
                    </Box>
                    {items.length - 1 !== index ? <Divider /> : null}
                  </Fragment>
                ))}
              </Card>
            </Grid.Item>
          </Grid>
        </Stack>
      </Grid.Item>
      {showImage && (
        <Grid.Item justifySelf="center">
          <Image maxWidth="100%" height="auto" sources={image.sources} src={image.src ?? ''} alt={image.alt ?? ''} />
        </Grid.Item>
      )}
    </Grid>
  );
};

export default ContactMethods;
